import { __awaiter, __generator, __spreadArray } from "tslib";
import { d, n, w, Xhr } from '@tinkoff/statist-page-data';
import { MIN_OMITTABLE_TIME_RANGE } from '../constants/settings';
import { gzip } from '../utils';
import { EventEmitter } from '../utils/event-emitter';
import { LOCAL_STORAGE_ENABLED_GZIP } from '../constants/labels';
import * as LocalStorage from '../storages/local-storage';
import * as MessageStorage from '../utils/message-storage';
var Transport = /** @class */ (function () {
    function Transport(endpointUrl, options) {
        var _this = this;
        var _a, _b;
        this.timer = null;
        this.eventEmitter = new EventEmitter();
        this.destroy = function () {
            d.removeEventListener('visibilitychange', _this.handleVisibilityChange);
            w.removeEventListener('pagehide', _this.handlePageHide);
        };
        this.handleVisibilityChange = function () {
            if (d.visibilityState === 'hidden') {
                _this.sendByBeacon();
            }
            else {
                _this.processSavedMessages();
            }
        };
        this.handlePageHide = function () {
            _this.sendByBeacon();
        };
        this.pushEventsUrl = "".concat(endpointUrl, "/gateway/v1/events");
        this.batchSize = options.batchSize;
        this.pushInterval = options.pushInterval;
        this.pushTimeout = (_a = options.pushTimeout) !== null && _a !== void 0 ? _a : options.pushInterval;
        this.savedMessagesSize = options.savedMessagesSize;
        this.eventsJsonSizeLimit = options.eventsJsonSizeLimit;
        this.gzipDisabled = !(LocalStorage.get(LOCAL_STORAGE_ENABLED_GZIP) === 'true');
        this.apiKey = (_b = options.apiKey) !== null && _b !== void 0 ? _b : '';
        if ('visibilityState' in d) {
            d.addEventListener('visibilitychange', this.handleVisibilityChange, false);
        }
        w.addEventListener('pagehide', this.handlePageHide, false);
        this.events = this.excludeOutdatedMessages(MessageStorage.get());
        this.sequence = this.getMaxSequence(this.events);
        if (this.events.length > 0) {
            MessageStorage.clear();
            this.startPush();
        }
    }
    Transport.prototype.handleApiError = function (error) {
        var _this = this;
        var _a, _b;
        if (error.status < 200 || error.status >= 500) {
            this.saveMessages(error.events);
            this.startPush();
            return;
        }
        if (error.response) {
            try {
                var parsed_1 = JSON.parse(error.response);
                if (parsed_1.message && ((_a = parsed_1.details) === null || _a === void 0 ? void 0 : _a.errors)) {
                    parsed_1.details.errors.forEach(function (response) {
                        if (response) {
                            _this.eventEmitter.emit('error', Object.assign(new Error(parsed_1.message), {
                                status: error.status,
                                response: response,
                            }));
                        }
                    });
                    return;
                }
            }
            catch (err) { }
        }
        this.eventEmitter.emit('error', Object.assign(new Error((_b = error.response) !== null && _b !== void 0 ? _b : error.message), {
            status: error.status,
        }));
    };
    Transport.prototype.saveMessages = function (messages) {
        if (messages.length === 0) {
            return true;
        }
        var savedMessages = MessageStorage.get();
        var messagesToSave = __spreadArray(__spreadArray([], savedMessages, true), messages, true);
        if (messagesToSave.length > this.savedMessagesSize) {
            var eventsCountToDelete = messagesToSave.length - this.savedMessagesSize;
            messagesToSave.splice(0, eventsCountToDelete);
        }
        return MessageStorage.save(messagesToSave);
    };
    Transport.prototype.excludeOutdatedMessages = function (messages) {
        var currentTimestamp = Date.now();
        return messages.filter(function (message) {
            var messageTimestamp = message.eventParameters.clientEventTimestamp;
            return currentTimestamp - messageTimestamp < MIN_OMITTABLE_TIME_RANGE;
        });
    };
    Transport.prototype.processSavedMessages = function () {
        this.events = __spreadArray(__spreadArray([], MessageStorage.get(), true), this.events, true);
        if (this.events.length > 0) {
            MessageStorage.clear();
            this.sequence = this.getMaxSequence(this.events);
            this.startPush();
        }
    };
    Transport.prototype.getEventsJsonSize = function () {
        var json = JSON.stringify(this.events);
        return new Blob([json]).size;
    };
    Transport.prototype.on = function (instanceEvent, handler) {
        this.eventEmitter.on(instanceEvent, handler);
    };
    Transport.prototype.off = function (instanceEvent, handler) {
        this.eventEmitter.off(instanceEvent, handler);
    };
    /**
     * Порционно отправляет события согласно `options.batchSize` с периодичностью `options.pushInterval`.
     * При этом отправляет все события сразу, если их размер превысит лимит `options.eventsJsonSizeLimit`.
     */
    Transport.prototype.startPush = function () {
        var _this = this;
        if (this.events.length === 0)
            return;
        var tryPushEvents = function (size) {
            return (_this.isReady() ? _this.pushEvents(size) : Promise.resolve()).then(function () {
                _this.timer = null;
                _this.startPush();
            }, function (error) {
                _this.timer = null;
                _this.handleApiError(error);
            });
        };
        var isOverLimit = this.getEventsJsonSize() >= this.eventsJsonSizeLimit;
        if (isOverLimit) {
            tryPushEvents(this.events.length);
            return;
        }
        if (this.timer)
            return;
        this.timer = setTimeout(function () { return tryPushEvents(_this.batchSize); }, this.pushInterval);
    };
    Transport.prototype.pushEvents = function (size) {
        return __awaiter(this, void 0, void 0, function () {
            var events;
            return __generator(this, function (_a) {
                events = this.events.splice(0, size);
                return [2 /*return*/, this.sendByXhr(events)];
            });
        });
    };
    Transport.prototype.updateTimestamp = function (events) {
        var now = Date.now();
        for (var _i = 0, events_1 = events; _i < events_1.length; _i++) {
            var event_1 = events_1[_i];
            // eslint-disable-next-line no-param-reassign
            event_1.eventParameters.clientUploadTimestamp = now;
        }
    };
    Transport.prototype.updateSequence = function (message) {
        // eslint-disable-next-line no-param-reassign
        message.eventParameters.sequence = ++this.sequence;
    };
    Transport.prototype.getMaxSequence = function (messages) {
        var maxSequence = 0;
        messages.forEach(function (_a) {
            var sequence = _a.eventParameters.sequence;
            if (sequence > maxSequence) {
                maxSequence = sequence;
            }
        });
        return maxSequence;
    };
    Transport.prototype.sendByXhr = function (events) {
        var _this = this;
        if (!Xhr) {
            return Promise.reject();
        }
        if (!events.length) {
            return Promise.resolve();
        }
        this.updateTimestamp(events);
        return new Promise(function (resolve, reject) {
            var r = new Xhr();
            var onload = function () {
                if (r.status >= 200 && r.status <= 204) {
                    resolve();
                    return;
                }
                var error = Object.assign(new Error(r.responseText), {
                    status: r.status,
                    response: r.responseText,
                    events: events,
                });
                reject(error);
            };
            r.onerror = onload;
            r.onload = onload;
            r.ontimeout = onload;
            r.timeout = _this.pushTimeout;
            var jsonEvents = JSON.stringify(events);
            if (_this.gzipDisabled) {
                _this.sendEventsJSON(jsonEvents, r);
            }
            else {
                _this.sendEventsGZip(jsonEvents, r);
            }
        });
    };
    Transport.prototype.sendEventsJSON = function (json, r) {
        r.open('POST', this.pushEventsUrl, true);
        r.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');
        if (this.apiKey) {
            r.setRequestHeader('X-Api-Key', this.apiKey);
        }
        r.send(json);
    };
    Transport.prototype.sendEventsGZip = function (json, r) {
        var _this = this;
        gzip
            .compress(json)
            .then(function (compressedEvents) {
            try {
                r.open('POST', _this.pushEventsUrl, true);
                r.setRequestHeader('Content-Type', 'application/gzip');
                r.setRequestHeader('Content-Encoding', 'gzip');
                if (_this.apiKey) {
                    r.setRequestHeader('X-Api-Key', _this.apiKey);
                }
                r.send(compressedEvents);
            }
            catch (_a) { }
        })
            .catch(function () {
            _this.sendEventsJSON(json, r);
        });
    };
    Transport.prototype.sendByBeacon = function () {
        var allEvents = this.events;
        this.events = [];
        if (!allEvents.length) {
            return;
        }
        if (!n.onLine) {
            this.saveMessages(allEvents);
            return;
        }
        this.updateTimestamp(allEvents);
        var response = n.sendBeacon(this.pushEventsUrl, JSON.stringify(allEvents));
        if (!response) {
            this.saveMessages(allEvents);
        }
    };
    Transport.prototype.forceSend = function () {
        this.sendByBeacon();
    };
    Transport.prototype.send = function (event) {
        this.updateSequence(event);
        this.events.push(event);
        this.startPush();
    };
    Transport.prototype.isReady = function () {
        return true;
    };
    return Transport;
}());
export { Transport };
//# sourceMappingURL=transport.js.map